import React, { useState, useEffect, useRef } from 'react';
import {
  CheckCircle2, ArrowRight, ArrowLeft, Loader2, Shield, Building2,
  Sparkles, CreditCard, Lock, AlertTriangle, Copy, Check, Zap, Crown, Star,
  FileText, User, MapPin, ClipboardList, Sun, Moon, Database, KeyRound, Boxes, Mail
} from 'lucide-react';
import LanguageSwitcher from '../i18n/LanguageSwitcher';
import { useT } from '../i18n/I18nContext';
import { API } from '../lib/api';
import './PublicSignup.css';

const THEME_KEY = 'rc_signup_theme';

/**
 * Public Signup + Landing flow (RetailCLIQ brand theme, light/dark).
 *  mode → landing (tier) → form → verify (OTP) → payment (paid tiers) → provisioning → success
 */
export default function PublicSignup({ initialStep = 'mode', onExitToLogin }) {
  const t = useT();
  const [theme, setTheme]       = useState(() => localStorage.getItem(THEME_KEY) || 'light');
  const [step, setStep]         = useState(initialStep);
  const [mode, setMode]         = useState(null); // 'simple' | 'detailed'
  const [tiers, setTiers]       = useState([]);
  const [industries, setIndustries] = useState([]);
  const [tier, setTier]         = useState(null);
  const [form, setForm]         = useState({
    companyName: '', tradeName: '', industryCode: '',
    adminName: '', adminEmail: '', adminPhone: '',
    country: 'IND', branchCount: 1, expectedUsers: 1
  });
  const [appState, setAppState] = useState({ applicationId: null, mockRazorpay: null, paymentRequired: false });
  const [paymentForceFail, setPaymentForceFail] = useState(false);
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [copied, setCopied]     = useState(false);
  const [otpState, setOtpState] = useState({ devEmailOtp: null, devPhoneOtp: null, maskedEmail: '', maskedPhone: '', sent: false });

  useEffect(() => { localStorage.setItem(THEME_KEY, theme); }, [theme]);

  useEffect(() => {
    (async () => {
      try {
        const [pt, ind] = await Promise.all([
          fetch(`${API}/signup/pricing-tiers`).then(r => r.json()),
          fetch(`${API}/signup/industries`).then(r => r.json())
        ]);
        setTiers(pt.tiers || []);
        setIndustries(ind.industries || []);
      } catch (e) {
        setError(`Cannot reach signup API: ${e.message}`);
      }
    })();
  }, []);

  const _log = (stage, data = {}) => { console.debug(`[SIGNUP] ${stage}`, data); };

  const submitForm = async (e) => {
    e?.preventDefault();
    if (!form.companyName || !form.adminEmail || !form.adminName || !form.industryCode) {
      setError('Please fill all required fields.');
      return;
    }
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/signup/initiate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tierCode: tier.code })
      });
      const d = await r.json();
      _log('INITIATE', d);
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      setAppState({ applicationId: d.applicationId, mockRazorpay: d.mockRazorpay || null, paymentRequired: !!d.paymentRequired });
      setStep('verify');
      await sendOtp(d.applicationId);
    } catch (e) {
      setError(`Submit failed: ${e.message}`);
    } finally { setLoading(false); }
  };

  const sendOtp = async (applicationId) => {
    try {
      const r = await fetch(`${API}/signup/otp/send`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId })
      });
      const d = await r.json();
      _log('OTP SEND', d);
      if (r.status === 429 && d.error === 'RATE_LIMITED') {
        setError(`Please wait ${d.waitSeconds || 30} seconds before requesting a new code.`);
        return { rateLimited: true, waitSeconds: d.waitSeconds || 30 };
      }
      if (r.ok && d.ok) {
        setError('');
        setOtpState({
          devEmailOtp: d.devEmailOtp || null, devPhoneOtp: d.devPhoneOtp || null,
          emailDevMode: d.emailDevMode || false, phoneDevMode: d.phoneDevMode || false,
          maskedEmail: d.maskedEmail || '', maskedPhone: d.maskedPhone || '', sent: true
        });
        return { rateLimited: false, waitSeconds: 30 };
      }
      setError(d.error || 'Failed to send OTP');
      return { rateLimited: false, waitSeconds: 30 };
    } catch (e) {
      setError('Failed to send OTP: ' + e.message);
      return { rateLimited: false, waitSeconds: 30 };
    }
  };

  const verifyOtp = async (emailOtp) => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/signup/otp/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId: appState.applicationId, emailOtp })
      });
      const d = await r.json();
      _log('OTP VERIFY', d);
      if (!r.ok || d.error) throw new Error(d.message || d.error || `HTTP ${r.status}`);
      if (appState.paymentRequired) {
        setStep('payment');
      } else {
        setStep('provisioning');
        await completeSignup(appState.applicationId);
      }
    } catch (e) {
      setError(e.message);
    } finally { setLoading(false); }
  };

  const verifyMockPayment = async () => {
    setLoading(true); setError('');
    try {
      await new Promise(r => setTimeout(r, 1500));
      const r = await fetch(`${API}/signup/mock-payment/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: appState.mockRazorpay.orderId, paymentId: `pay_mock_${Date.now()}`, forceFail: paymentForceFail })
      });
      const d = await r.json();
      _log('PAYMENT', d);
      if (!r.ok || !d.verified) throw new Error(d.reason || d.error || 'Payment failed');
      setStep('provisioning');
      await completeSignup(appState.applicationId);
    } catch (e) {
      setError(`Payment failed: ${e.message}`);
      setLoading(false);
    }
  };

  const completeSignup = async (applicationId) => {
    setLoading(true); setError('');
    try {
      await new Promise(r => setTimeout(r, 1200));
      const r = await fetch(`${API}/signup/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ applicationId })
      });
      const d = await r.json();
      _log('COMPLETE', d);
      if (!r.ok || !d.success) throw new Error(d.error || d.message || `HTTP ${r.status}`);
      setResult(d);
      setStep('success');
    } catch (e) {
      setError(`Provisioning failed: ${e.message}`);
      setStep('payment');
    } finally { setLoading(false); }
  };

  const selectTier = (tr) => { setTier(tr); setStep('form'); setError(''); };
  const selectMode = (m) => { setMode(m); setStep('landing'); setError(''); };
  const reset = () => {
    setStep('mode'); setMode(null); setTier(null); setResult(null); setError('');
    setForm({ companyName: '', tradeName: '', industryCode: '', adminName: '', adminEmail: '', adminPhone: '', country: 'IND', branchCount: 1, expectedUsers: 1 });
    setAppState({ applicationId: null, mockRazorpay: null, paymentRequired: false });
    setOtpState({ devEmailOtp: null, devPhoneOtp: null, maskedEmail: '', maskedPhone: '', sent: false });
  };

  const dark = theme === 'dark';
  return (
    <div className={`rc-signup${dark ? ' rc-dark' : ''}`} data-testid="public-signup-root" data-theme={theme}>
      <header className="rc-header">
        <div className="rc-header-brand">
          <img src={dark ? '/assets/retailcliq-logo-inverse.png' : '/assets/retailcliq-logo-full.png'} alt="RetailCLIQ" onError={e => { e.target.style.display = 'none'; }} />
          <span className="rc-header-tag">{t('signup.page_title', 'Sign up')}</span>
        </div>
        <div className="rc-header-actions">
          <LanguageSwitcher compact />
          <button type="button" className={`rc-icon-btn${dark ? ' active' : ''}`} onClick={() => setTheme(dark ? 'light' : 'dark')} data-testid="signup-theme-toggle" title={dark ? 'Switch to light theme' : 'Switch to dark theme'} aria-label="Toggle theme">
            {dark ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <button onClick={onExitToLogin} data-testid="signup-exit-to-login" className="rc-btn rc-btn-ghost rc-btn-sm">
            {t('auth.login_title', 'Already have an account? Log in')}
          </button>
        </div>
      </header>

      <main className="rc-main">
        {error && (
          <div data-testid="signup-error" className="rc-error"><AlertTriangle size={15} /> <span>{error}</span></div>
        )}

        <Steps step={step} mode={mode} />

        {step === 'mode'         && <ModeSelect onSelect={selectMode} />}
        {step === 'landing'      && <Landing      tiers={tiers} onSelect={selectTier} onBack={() => setStep('mode')} />}
        {step === 'form'         && <SignupForm   tier={tier} form={form} setForm={setForm} mode={mode} industries={industries} onBack={() => setStep('landing')} onSubmit={submitForm} loading={loading} />}
        {step === 'verify'       && <OtpVerify    otpState={otpState} loading={loading} error={error} onVerify={verifyOtp} onResend={() => sendOtp(appState.applicationId)} onBack={() => setStep('form')} />}
        {step === 'payment'      && <MockPayment  tier={tier} mockRazorpay={appState.mockRazorpay} forceFail={paymentForceFail} setForceFail={setPaymentForceFail} onPay={verifyMockPayment} onBack={() => setStep('verify')} loading={loading} />}
        {step === 'provisioning' && <Provisioning tier={tier} />}
        {step === 'success'      && <Success      result={result} tier={tier} onExitToLogin={onExitToLogin} onAnother={reset} copied={copied} setCopied={setCopied} />}
      </main>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function Steps({ step, mode }) {
  const items = [
    { k: 'mode',         label: 'Mode' },
    { k: 'landing',      label: 'Plan' },
    { k: 'form',         label: mode === 'detailed' ? 'KYC details' : 'Details' },
    { k: 'verify',       label: 'Verify' },
    { k: 'payment',      label: 'Payment' },
    { k: 'provisioning', label: 'Provision' },
    { k: 'success',      label: 'Welcome' }
  ];
  const cur = items.findIndex(i => i.k === step);
  return (
    <div className="rc-steps" data-testid="signup-steps">
      {items.map((it, i) => {
        const done = i < cur, active = i === cur;
        return (
          <React.Fragment key={it.k}>
            {i > 0 && <span className={`rc-step-line${done || active ? ' done' : ''}`} />}
            <div className={`rc-step${done ? ' done' : ''}${active ? ' active' : ''}`} data-testid={`signup-step-${it.k}`}>
              <span className="rc-step-dot">{done ? <Check size={11} /> : i + 1}</span>
              {it.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function OtpVerify({ otpState, loading, error: parentError, onVerify, onResend, onBack }) {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [localError, setLocalError] = useState('');
  const refs = useRef([]);
  const code = digits.join('');

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    const r = await onResend();
    const wait = r?.waitSeconds || 30;
    setResendCooldown(wait);
    const tick = setInterval(() => { setResendCooldown(s => { if (s <= 1) { clearInterval(tick); return 0; } return s - 1; }); }, 1000);
  };

  const fillFrom = (i, text) => {
    const clean = text.replace(/\D/g, '');
    if (!clean) return;
    const next = [...digits];
    clean.slice(0, 6 - i).split('').forEach((ch, k) => { next[i + k] = ch; });
    setDigits(next);
    refs.current[Math.min(i + clean.length, 5)]?.focus();
  };
  const setAt = (i, v) => {
    const clean = v.replace(/\D/g, '');
    if (clean.length > 1) return fillFrom(i, clean);
    const next = [...digits]; next[i] = clean; setDigits(next);
    if (clean && i < 5) refs.current[i + 1]?.focus();
  };
  const onPaste = (i, e) => {
    e.preventDefault();
    fillFrom(i, (e.clipboardData || window.clipboardData).getData('text') || '');
  };
  const onKey = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
  };

  const handleSubmit = (e) => {
    e.preventDefault(); setLocalError('');
    if (code.length !== 6) { setLocalError('Enter the 6-digit email code'); return; }
    onVerify(code);
  };

  const err = parentError || localError;
  return (
    <form onSubmit={handleSubmit} className="rc-card rc-card-xs" data-testid="signup-otp-verify">
      <div className="rc-card-head"><span className="rc-ico"><Shield size={18} /></span><h2>Verify your email</h2></div>
      <p className="rc-sub">We sent a 6-digit code to <strong>{otpState.maskedEmail}</strong>. Enter it below to continue.</p>
      {err && <div className="rc-error" style={{ margin: '0 0 12px' }}><AlertTriangle size={13} /> {err}</div>}

      {otpState.emailDevMode && <span className="rc-pill warn">Dev mode — SMTP not configured</span>}
      <div className="rc-otp" data-testid="signup-email-otp">
        {digits.map((d, i) => (
          <input key={i} ref={el => (refs.current[i] = el)} value={d} inputMode="numeric" autoComplete="one-time-code"
            onChange={e => setAt(i, e.target.value)} onKeyDown={e => onKey(i, e)} onPaste={e => onPaste(i, e)} onFocus={e => e.target.select()}
            autoFocus={i === 0} data-testid={`signup-otp-input-${i + 1}`} aria-label={`Digit ${i + 1}`} />
        ))}
      </div>
      {otpState.devEmailOtp && <div className="rc-dev-otp" data-testid="dev-email-otp"><KeyRound size={13} /> Dev OTP: {otpState.devEmailOtp}</div>}

      <div className="rc-actions">
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" onClick={onBack} className="rc-btn rc-btn-ghost rc-btn-sm" data-testid="signup-otp-back"><ArrowLeft size={13} /> Back</button>
          <button type="button" onClick={handleResend} disabled={loading || resendCooldown > 0} className="rc-btn rc-btn-ghost rc-btn-sm" data-testid="signup-otp-resend">
            {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
          </button>
        </div>
        <button type="submit" className="rc-btn rc-btn-primary" disabled={loading || code.length < 6} data-testid="signup-otp-submit">
          {loading ? <><Loader2 size={14} className="rc-spin" /> Verifying…</> : <>Verify & continue <ArrowRight size={14} /></>}
        </button>
      </div>
    </form>
  );
}

function ModeSelect({ onSelect }) {
  const modes = [
    { key: 'simple', icon: <Zap size={22} />, title: 'Simple signup', subtitle: 'Quick-start: pick a plan, enter details, verify by OTP and your workspace is provisioned.',
      steps: ['Select plan & industry', 'Enter contact details', 'Email OTP verification', 'Auto-provision + activation email'], cta: 'Start simple signup' },
    { key: 'detailed', icon: <ClipboardList size={22} />, title: 'Detailed signup + KYC', subtitle: 'Full enterprise onboarding: business registration, authorised officer, address and KYC.',
      steps: ['Business information', 'Authorised officer', 'Registered address', 'KYC review + OTP + provision'], cta: 'Start detailed signup' },
  ];
  return (
    <div data-testid="mode-select">
      <span className="rc-eyebrow"><Sparkles size={12} /> Tenant onboarding</span>
      <h1 className="rc-h1">Set up your <em>RetailCLIQ</em> workspace in minutes.</h1>
      <p className="rc-lead">Choose how you'd like to sign up. Both paths provision your ERP tenant instantly after email verification.</p>
      <div className="rc-mode-grid">
        {modes.map(m => (
          <div key={m.key} className="rc-mode" data-testid={`mode-card-${m.key}`} onClick={() => onSelect(m.key)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && onSelect(m.key)}>
            <span className="rc-mode-ico">{m.icon}</span>
            <h3>{m.title}</h3>
            <p>{m.subtitle}</p>
            <ol>{m.steps.map((s, i) => <li key={i}><span>{i + 1}</span>{s}</li>)}</ol>
            <button type="button" className="rc-btn rc-btn-primary" onClick={e => { e.stopPropagation(); onSelect(m.key); }} data-testid={`mode-select-${m.key}`}>
              {m.cta} <ArrowRight size={14} />
            </button>
          </div>
        ))}
      </div>
      <p className="rc-footnote">Need help? <a href="mailto:support@retailcliq.in">Contact support</a></p>
    </div>
  );
}

function Landing({ tiers, onSelect, onBack }) {
  return (
    <div>
      {onBack && <button onClick={onBack} data-testid="landing-back" className="rc-btn rc-btn-link" style={{ marginBottom: 22 }}><ArrowLeft size={13} /> Back to signup options</button>}
      <span className="rc-eyebrow"><Boxes size={12} /> Plans & pricing</span>
      <h1 className="rc-h1">Choose the plan that <em>fits</em> your business.</h1>
      <p className="rc-lead">Start free and scale to enterprise. Every plan includes a 15-day trial after activation.</p>
      <div className="rc-tier-grid">
        {tiers.map(tr => <TierCard key={tr.code} tier={tr} onSelect={() => onSelect(tr)} />)}
      </div>
    </div>
  );
}

function TierCard({ tier, onSelect }) {
  const isCustom  = tier.billingCycle === 'custom';
  const isFree    = tier.price === 0 && !isCustom;
  const isPopular = tier.badge === 'Most Popular';
  const Icon = tier.level >= 5 ? Crown : tier.level === 4 ? Star : tier.level === 3 ? Zap : Sparkles;
  return (
    <div className={`rc-tier${isPopular ? ' pop' : ''}`} data-testid={`tier-card-${tier.code.toLowerCase()}`}>
      {tier.badge && <span className="rc-tier-badge">{tier.badge}</span>}
      <div className="rc-tier-name"><Icon size={18} /> {tier.name}</div>
      <p className="rc-tier-tag">{tier.tagline}</p>
      <div className="rc-price">
        <strong className={isCustom ? 'custom' : ''}>{isCustom ? 'Contact sales' : isFree ? 'Free' : `₹${tier.price}`}</strong>
        {!isFree && !isCustom && <span>/ {tier.billingCycle}</span>}
      </div>
      <ul className="rc-feat">
        {tier.features.map((f, i) => <li key={i}><CheckCircle2 size={13} /> {f}</li>)}
      </ul>
      <button onClick={onSelect} className={`rc-btn ${isPopular ? 'rc-btn-primary' : 'rc-btn-ghost'}`} data-testid={`tier-cta-${tier.code.toLowerCase()}`}>
        {tier.cta || 'Select'} <ArrowRight size={13} />
      </button>
    </div>
  );
}

function SignupForm({ tier, form, setForm, mode, industries, onBack, onSubmit, loading }) {
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [countries, setCountries] = useState([]);
  const [fiscalInfo, setFiscalInfo] = useState(null);

  useEffect(() => {
    fetch(`${API}/country-fiscal`).then(r => r.json()).then(data => {
      if (!Array.isArray(data)) return;
      const M = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      setCountries(data.map(c => ({
        code: c.CountryCode || c.countrycode, name: c.CountryName || c.country_name,
        currency: c.CurrencyCode || c.currency_code,
        fiscal: c.fiscal_label || (c.FiscalStart ? `${M[c.FiscalStart - 1]} – ${M[(c.FiscalEnd || 12) - 1]}` : ''),
      })).filter(c => c.code));
    }).catch(() => {});
  }, []);
  useEffect(() => {
    if (!form.country) return;
    setFiscalInfo(countries.find(c => c.code === form.country.toUpperCase()) || null);
  }, [form.country, countries]);

  return (
    <form onSubmit={onSubmit} noValidate className="rc-card rc-card-narrow" data-testid="signup-form">
      <div className="rc-card-head">
        <span className="rc-ico"><Building2 size={18} /></span>
        <h2>{mode === 'detailed' ? 'Business & KYC details' : 'Your details'}</h2>
        <span className="rc-pill" style={{ marginLeft: 'auto' }}>{tier.name}</span>
        {mode === 'detailed' && <span className="rc-pill neutral">KYC</span>}
      </div>
      <p className="rc-sub">Tell us about your company and the administrator who will own this workspace.</p>

      <div className="rc-row">
        <Field label="Company name *"><input className="rc-input" required value={form.companyName} onChange={e => set('companyName', e.target.value)} data-testid="signup-company-name" /></Field>
        <Field label="Trade name (optional)"><input className="rc-input" value={form.tradeName} onChange={e => set('tradeName', e.target.value)} data-testid="signup-trade-name" /></Field>
      </div>
      <div className="rc-row">
        <Field label="Industry *">
          <select className="rc-input" required value={form.industryCode} onChange={e => set('industryCode', e.target.value)} data-testid="signup-industry">
            <option value="">Select industry…</option>
            {industries.map(i => <option key={i.IndustryCode} value={i.IndustryCode}>{i.IndustryName}</option>)}
          </select>
        </Field>
        <Field label="Country">
          {countries.length > 0 ? (
            <select className="rc-input" value={form.country} onChange={e => set('country', e.target.value)} data-testid="signup-country">
              <option value="">Select country…</option>
              {countries.map(c => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
            </select>
          ) : (
            <input className="rc-input" value={form.country} onChange={e => set('country', e.target.value)} data-testid="signup-country" />
          )}
          {fiscalInfo && <div className="rc-hint" data-testid="signup-fiscal-hint">Fiscal year: {fiscalInfo.fiscal} · Currency: {fiscalInfo.currency}</div>}
        </Field>
      </div>
      <div className="rc-row">
        <Field label="Admin name *"><input className="rc-input" required value={form.adminName} onChange={e => set('adminName', e.target.value)} data-testid="signup-admin-name" /></Field>
        <Field label="Admin email *"><input className="rc-input" type="email" required value={form.adminEmail} onChange={e => set('adminEmail', e.target.value)} data-testid="signup-admin-email" /></Field>
      </div>
      <div className="rc-row">
        <Field label="Phone (optional)"><input className="rc-input" value={form.adminPhone} onChange={e => set('adminPhone', e.target.value)} data-testid="signup-admin-phone" /></Field>
        <Field label="Expected users"><input className="rc-input" type="number" min={1} value={form.expectedUsers} onChange={e => set('expectedUsers', e.target.value)} data-testid="signup-expected-users" /></Field>
      </div>

      {mode === 'detailed' && (
        <>
          <div className="rc-section"><FileText size={13} /> Business information</div>
          <div className="rc-row">
            <Field label="Business registration no."><input className="rc-input" value={form.businessRegNo || ''} onChange={e => set('businessRegNo', e.target.value)} data-testid="signup-business-reg" placeholder="CIN / LLPIN / Registration No." /></Field>
            <Field label="GST / Tax ID"><input className="rc-input" value={form.gstNo || ''} onChange={e => set('gstNo', e.target.value)} data-testid="signup-gst-no" placeholder="e.g. 29ABCDE1234F1Z5" /></Field>
          </div>
          <div className="rc-row">
            <Field label="Business type">
              <select className="rc-input" value={form.businessType || ''} onChange={e => set('businessType', e.target.value)} data-testid="signup-business-type">
                <option value="">Select type…</option>
                {['Pvt Ltd', 'LLP', 'Sole Proprietor', 'Partnership', 'Public Ltd', 'Trust / NGO', 'Other'].map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </Field>
            <Field label="No. of branches"><input className="rc-input" type="number" min={1} value={form.branchCount} onChange={e => set('branchCount', e.target.value)} data-testid="signup-branch-count" /></Field>
          </div>
          <div className="rc-section"><User size={13} /> Authorised officer</div>
          <div className="rc-row">
            <Field label="Officer designation"><input className="rc-input" value={form.officerDesignation || ''} onChange={e => set('officerDesignation', e.target.value)} data-testid="signup-officer-designation" placeholder="e.g. MD / CEO / Director" /></Field>
            <Field label="Officer phone"><input className="rc-input" value={form.officerPhone || ''} onChange={e => set('officerPhone', e.target.value)} data-testid="signup-officer-phone" /></Field>
          </div>
          <div className="rc-section"><MapPin size={13} /> Registered address</div>
          <div className="rc-row">
            <Field label="Street / Area"><input className="rc-input" value={form.addressStreet || ''} onChange={e => set('addressStreet', e.target.value)} data-testid="signup-address-street" /></Field>
            <Field label="City"><input className="rc-input" value={form.addressCity || ''} onChange={e => set('addressCity', e.target.value)} data-testid="signup-address-city" /></Field>
          </div>
          <div className="rc-row">
            <Field label="State / Province"><input className="rc-input" value={form.addressState || ''} onChange={e => set('addressState', e.target.value)} data-testid="signup-address-state" /></Field>
            <Field label="PIN / ZIP code"><input className="rc-input" value={form.addressPin || ''} onChange={e => set('addressPin', e.target.value)} data-testid="signup-address-pin" /></Field>
          </div>
          <div className="rc-note" style={{ marginTop: 18 }}>KYC document verification (ID proof, address proof, business certificate) is processed by the RetailCLIQ team within 1–2 business days after submission.</div>
        </>
      )}

      <div className="rc-actions">
        <button type="button" onClick={onBack} className="rc-btn rc-btn-ghost" data-testid="signup-form-back"><ArrowLeft size={13} /> Back</button>
        <button type="submit" className="rc-btn rc-btn-primary grow" disabled={loading} data-testid="signup-form-submit">
          {loading ? <><Loader2 size={14} className="rc-spin" /> Submitting…</> : <>Continue <ArrowRight size={14} /></>}
        </button>
      </div>
    </form>
  );
}

function MockPayment({ tier, mockRazorpay, forceFail, setForceFail, onPay, onBack, loading }) {
  if (!mockRazorpay) return <div className="rc-card rc-card-xs"><Loader2 size={14} className="rc-spin" /> Loading payment session…</div>;
  return (
    <div className="rc-card rc-card-xs" data-testid="signup-mock-payment">
      <div className="rc-card-head">
        <span className="rc-ico"><CreditCard size={18} /></span>
        <h2>Payment</h2>
        <span className="rc-pill warn" style={{ marginLeft: 'auto' }}>Mocked · Razorpay test</span>
      </div>
      <p className="rc-sub">Review your order. This is a test-mode simulation — no money is charged.</p>
      <div className="rc-box">
        <div className="rc-dict"><span>Plan</span><strong>{tier.name}</strong></div>
        <div className="rc-dict"><span>Amount</span><strong>{mockRazorpay.currency} {tier.price}</strong></div>
        <div className="rc-dict"><span>Order ID</span><code className="rc-code">{mockRazorpay.orderId}</code></div>
        <div className="rc-dict"><span>Description</span><span>{mockRazorpay.description}</span></div>
      </div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, fontSize: 12, color: 'var(--text-muted)' }}>
        <input type="checkbox" checked={forceFail} onChange={e => setForceFail(e.target.checked)} data-testid="signup-payment-force-fail" />
        Force failure (QA only — simulate declined payment)
      </label>
      <div className="rc-actions">
        <button type="button" onClick={onBack} disabled={loading} className="rc-btn rc-btn-ghost" data-testid="signup-payment-back"><ArrowLeft size={13} /> Back</button>
        <button onClick={onPay} disabled={loading} className="rc-btn rc-btn-primary grow" data-testid="signup-payment-pay">
          {loading ? <><Loader2 size={14} className="rc-spin" /> Processing…</> : <><Lock size={13} /> Pay ₹{tier.price} (mock)</>}
        </button>
      </div>
    </div>
  );
}

function Provisioning({ tier }) {
  const dedicated = tier?.dbModel?.startsWith('dedicated');
  return (
    <div className="rc-card rc-card-xs rc-center" data-testid="signup-provisioning">
      <Loader2 size={40} className="rc-spin" style={{ color: 'var(--brand)', margin: '8px auto 16px' }} />
      <h2 style={{ fontSize: 24, margin: 0 }}>Provisioning your tenant…</h2>
      <p className="rc-sub" style={{ marginTop: 8 }}>{dedicated ? `Cloning a dedicated database for ${tier.name}. This usually takes under a minute.` : `Setting up your account on the shared RetailCLIQ ${tier.name} tier.`}</p>
      <div className="rc-progress">
        <div><Check size={14} /> License generated</div>
        <div><Check size={14} /> Identity block recorded</div>
        <div><Check size={14} /> Modules resolved</div>
        <div><Database size={14} /> Database & users seeded</div>
      </div>
    </div>
  );
}

function Success({ result, tier, onExitToLogin, onAnother, copied, setCopied }) {
  const copy = (v) => { navigator.clipboard.writeText(v); setCopied(true); setTimeout(() => setCopied(false), 1500); };
  return (
    <div className="rc-card rc-card-narrow rc-center" data-testid="signup-success">
      <div className="rc-check"><Check size={34} strokeWidth={3} /></div>
      <h2 style={{ fontSize: 30, margin: 0 }}>Setup complete</h2>
      <p className="rc-sub" style={{ marginTop: 8 }}>Welcome to <strong>{tier.name}</strong>. Your tenant has been provisioned.</p>
      <div className="rc-client-id">
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase' }}>Client ID</div>
          <code data-testid="signup-success-client-id">{result.clientId}</code>
        </div>
        <button onClick={() => copy(result.clientId)} className="rc-btn rc-btn-ghost rc-btn-sm" data-testid="signup-success-copy-client">{copied ? <Check size={13} /> : <Copy size={13} />} {copied ? 'Copied' : 'Copy'}</button>
      </div>
      <div className="rc-box">
        <div className="rc-dict"><span>License ID</span><code className="rc-code">{result.licenseId}</code></div>
        {result.databaseName && <div className="rc-dict"><span>Database</span><code className="rc-code">{result.databaseName}</code></div>}
        <div className="rc-dict"><span>Tier</span><strong>{result.tierName}</strong></div>
      </div>
      <div className="rc-note" style={{ marginTop: 14, display: 'flex', gap: 10, alignItems: 'flex-start', textAlign: 'left' }} data-testid="signup-activation-note">
        <Mail size={15} style={{ flexShrink: 0, marginTop: 2, color: 'var(--brand)' }} />
        <span>{result.message || 'Check your email to activate your account. Your login credentials are sent after activation.'}</span>
      </div>
      <div className="rc-actions" style={{ justifyContent: 'center' }}>
        <button onClick={onAnother} className="rc-btn rc-btn-ghost" data-testid="signup-success-another">Add another tenant</button>
        <button onClick={onExitToLogin} className="rc-btn rc-btn-primary" data-testid="signup-success-login">Continue to login <ArrowRight size={14} /></button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <label className="rc-field">{label}{children}</label>;
}
